import React, { useState } from "react";
import { ChevronDown, ChevronUp, AlertCircle } from "lucide-react";

const TOPICS = [
  {
    id: "coercive-control",
    title: "Coercive Control",
    color: "#C86B55",
    summary: "A pattern of behaviour that seeks to take away your liberty or freedom, to strip away your sense of self.",
    sections: [
      {
        heading: "What is it?",
        content: "Coercive control is a pattern of intimidation, isolation, and domination. It may not always look like physical violence. It can include monitoring your movements, controlling your finances, isolating you from friends and family, or making you feel like you can't make decisions on your own."
      },
      {
        heading: "Signs to look for",
        content: "Constantly checking your phone or social media. Telling you what to wear. Making you feel guilty for spending time with friends or family. Controlling household money. Making all the decisions. Using threats — sometimes subtle ones. Making you doubt your own memory or feelings (gaslighting)."
      },
      {
        heading: "Remember",
        content: "Coercive control is recognised as a serious form of abuse. You are not imagining it. Your experience is valid. You deserve to feel free and safe in your relationships."
      }
    ]
  },
  {
    id: "financial-abuse",
    title: "Financial Abuse",
    color: "#4A6B53",
    summary: "When someone controls your ability to earn, use, or maintain financial resources.",
    sections: [
      {
        heading: "What is it?",
        content: "Financial abuse is when a partner or family member controls your ability to acquire, use, and maintain financial resources. It can happen to anyone, regardless of income level or background."
      },
      {
        heading: "Signs to look for",
        content: "Controlling how money is spent. Withholding money or giving an 'allowance'. Preventing you from working or sabotaging your job. Putting all bills or debt in your name. Hiding financial information. Making you account for every rupee spent."
      },
      {
        heading: "What you can do",
        content: "Keep copies of important financial documents if safe to do so. Consider opening a separate bank account. Talk to a trusted person about your situation. Many organisations offer financial counselling specifically for people experiencing abuse."
      }
    ]
  },
  {
    id: "cycle-of-abuse",
    title: "Cycle of Abuse",
    color: "#D2A679",
    summary: "Understanding the pattern can help you see what's happening more clearly.",
    sections: [
      {
        heading: "The pattern",
        content: "Abuse often follows a cycle: tension building, an incident, reconciliation (apologies, promises), and a calm period. Then the cycle repeats. Understanding this pattern doesn't mean it's your fault — it means you can recognise what's happening."
      },
      {
        heading: "Why it's confusing",
        content: "During the calm phase, things can feel 'normal' or even wonderful. This makes it hard to leave or to recognise the relationship as abusive. The good times can feel so real — and they are real — but they don't cancel out the harm."
      },
      {
        heading: "Breaking the cycle",
        content: "The cycle rarely stops on its own. It often escalates over time. Breaking free starts with recognising the pattern. You don't have to do anything right now except acknowledge what you see."
      }
    ]
  },
  {
    id: "love-bombing",
    title: "Love Bombing & Isolation",
    color: "#E5B95F",
    summary: "When excessive affection is used to gain control, and when your world gets smaller.",
    sections: [
      {
        heading: "Love bombing",
        content: "Love bombing is when someone overwhelms you with affection, attention, gifts, and promises very early in a relationship. It feels amazing — but it can be a manipulation tactic. The intensity isn't about love; it's about creating dependency."
      },
      {
        heading: "Isolation",
        content: "Gradually, they may start pulling you away from your friends and family. It might start small — 'I just want you to myself' — and grow into full isolation. Without your support network, you become more dependent on them."
      },
      {
        heading: "Red flags",
        content: "Moving extremely fast in the relationship. Getting upset when you spend time with others. Making you feel guilty for having your own life. Criticising your friends or family. Wanting to know where you are at all times. Creating an 'us vs. the world' dynamic."
      }
    ]
  },
  {
    id: "self-check",
    title: '"Is This Abuse?" Self-Check',
    color: "#8A918C",
    summary: "Sometimes it's hard to name what's happening. These questions might help.",
    sections: [
      {
        heading: "Ask yourself",
        content: "Do you feel afraid of your partner's reactions? Do you change your behaviour to avoid upsetting them? Do you feel isolated from friends and family? Do they control your finances? Do they check your phone or monitor your activities? Do they put you down or make you feel worthless? Do they threaten you — even subtly? Do you feel like you're 'walking on eggshells'?"
      },
      {
        heading: "If you answered yes",
        content: "If any of these resonate, what you're experiencing may be abuse. You are not overreacting. You are not being too sensitive. Abuse isn't just physical — it can be emotional, psychological, financial, and digital."
      },
      {
        heading: "What now?",
        content: "You don't have to do anything right now. Just knowing is a step. When you're ready, there are people who can help. You can talk to Sovha anytime, or explore resources in this app. You are not alone."
      }
    ]
  },
];

function TopicCard({ topic }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="bg-white rounded-[2rem] shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 overflow-hidden transition-all duration-500"
      data-testid={`topic-${topic.id}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-8 flex items-start gap-4"
        data-testid={`topic-toggle-${topic.id}`}
      >
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1"
          style={{ backgroundColor: topic.color + "15" }}
        >
          <AlertCircle size={20} style={{ color: topic.color }} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-xl text-earth-800 mb-1">{topic.title}</h3>
          <p className="text-earth-500 text-sm leading-relaxed">{topic.summary}</p>
        </div>
        <div className="flex-shrink-0 mt-1 text-earth-400">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {expanded && (
        <div className="px-8 pb-8 space-y-6 animate-fade-in-up">
          {topic.sections.map((section, i) => (
            <div key={i} className="pl-14">
              <h4 className="font-heading text-lg text-earth-800 mb-2">{section.heading}</h4>
              <p className="text-earth-600 text-sm leading-relaxed">{section.content}</p>
            </div>
          ))}
          <div className="pl-14">
            <div className="bg-sage-light rounded-2xl p-4 mt-2">
              <p className="text-sage text-sm font-medium">
                Remember: Knowledge is power. Understanding what's happening is a step toward freedom.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function UnderstandPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-20" data-testid="understand-page">
      <div className="text-center mb-12">
        <p className="text-sage text-sm uppercase tracking-[0.15em] font-bold mb-3">Understand What's Happening</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-earth-800 mb-4 tracking-tight">
          Knowledge is safety
        </h1>
        <p className="text-earth-600 text-base max-w-lg mx-auto">
          Sometimes naming what's happening is the hardest — and bravest — step.
          Read at your own pace. There's no rush.
        </p>
      </div>

      <div className="space-y-4">
        {TOPICS.map((topic) => (
          <TopicCard key={topic.id} topic={topic} />
        ))}
      </div>

      <div className="mt-12 bg-terracotta-light rounded-[2rem] p-8 text-center">
        <p className="text-terracotta font-heading text-lg mb-2">You are not alone</p>
        <p className="text-earth-600 text-sm">
          If anything here resonates, remember: it's not your fault. 
          You can talk to Sovha anytime, or reach out to someone you trust.
        </p>
      </div>
    </div>
  );
}
